import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './InwardEntry.css';

// Add improved suggestion dropdown styles
const suggestionStyles = `
.suggestion-list, .party-suggestion-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: auto;
  z-index: 1000;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(60,60,90,0.13), 0 1.5px 4px rgba(60,60,90,0.06);
  margin-top: 4px;
  width: 100%;
  min-width: 180px;
  max-width: 340px;
  max-height: 260px;
  overflow-y: auto;
  padding: 0;
  list-style: none;
  font-size: 15.5px;
  animation: fadeIn 0.18s;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-8px);}
  to { opacity: 1; transform: translateY(0);}
}
.suggestion-list li, .party-suggestion-list li {
  padding: 12px 18px;
  cursor: pointer;
  transition: background 0.13s, color 0.13s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #22223b;
  background: transparent;
  border: none;
}
.suggestion-list li:not(:last-child), .party-suggestion-list li:not(:last-child) {
  border-bottom: 1px solid #f3f4f6;
}
.suggestion-list li.active, .party-suggestion-list li.active,
.suggestion-list li:hover, .party-suggestion-list li:hover {
  background: linear-gradient(90deg, #e0e7ff 0%, #f3f4f6 100%);
  color: #3730a3;
}
.suggestion-list li b, .party-suggestion-list li b {
  font-weight: 700;
  color: #4f46e5;
  background: #e0e7ff;
  border-radius: 3px;
  padding: 0 2px;
}
.suggestion-list::-webkit-scrollbar, .party-suggestion-list::-webkit-scrollbar {
  width: 7px;
  background: #f3f4f6;
  border-radius: 10px;
}
.suggestion-list::-webkit-scrollbar-thumb, .party-suggestion-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 10px;
}
/* Ensure the parent is relative for correct absolute positioning */
.form-group, .party-name-advanced {
  position: relative;
}
`;

// Add improved Add Bill button styles
const addBillButtonStyles = {
  background: 'linear-gradient(90deg, #6366f1 0%, #818cf8 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 22px',
  fontWeight: 600,
  fontSize: '15px',
  marginTop: '8px',
  marginBottom: '8px',
  boxShadow: '0 2px 8px rgba(60,60,90,0.10)',
  cursor: 'pointer',
  transition: 'background 0.18s, color 0.18s',
  outline: 'none',
  letterSpacing: '0.5px',
};

// Inject the styles into the document head (only once)
if (typeof window !== 'undefined' && !window.__inwardEntrySuggestionStyles) {
  const style = document.createElement('style');
  style.innerHTML = suggestionStyles;
  document.head.appendChild(style);
  window.__inwardEntrySuggestionStyles = true;
}

const InwardEntryForm = ({ onEntryCreated }) => {
  const initialMaterial = { name: '', quantity: '', uom: '' };
  const initialBill = { bill_number: '', bill_amount: '' };
  const [serialNumber, setSerialNumber] = useState('');
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    party_name: '',
    entry_type: '',
    gross_weight: '',
    transport_mode: '',
    person_name: '',
    mobile_number: '',
    vehicle_number: '',
    vehicle_type: '',
    source_location: '',
    reference_number: '',
    po_number: '',
    time_in: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
    remarks: '',
    courier_name: '',
    docket_number: ''
  });
  const [bills, setBills] = useState([initialBill]);
  const [materials, setMaterials] = useState([initialMaterial]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [partySuggestions, setPartySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [materialSuggestions, setMaterialSuggestions] = useState([]);
  const [showMaterialSuggestions, setShowMaterialSuggestions] = useState(false);
  const [activeMaterialSuggestion, setActiveMaterialSuggestion] = useState(-1);
  const [sourceSuggestions, setSourceSuggestions] = useState([]);
  const [showSourceSuggestions, setShowSourceSuggestions] = useState(false);
  const [activeSourceSuggestion, setActiveSourceSuggestion] = useState(-1);

  useEffect(() => {
    const fetchSerialNumber = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/inward-entries/next-serial`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setSerialNumber(response.data.serial_number);
      } catch (err) {
        setError('Error fetching serial number');
      }
    };

    fetchSerialNumber();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Only allow digits and max 10 digits for mobile_number
    if (name === 'mobile_number') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePartyNameChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      party_name: value
    }));
    if (value.length >= 2) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/party-names?q=${encodeURIComponent(value)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPartySuggestions(response.data);
        setShowSuggestions(true);
        setActiveSuggestion(-1);
      } catch (err) {
        setPartySuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setPartySuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (name) => {
    setFormData(prev => ({
      ...prev,
      party_name: name
    }));
    setShowSuggestions(false);
  };

  const handlePartyInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 100);
  };

  const handlePartyInputKeyDown = (e) => {
    if (!showSuggestions || partySuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setActiveSuggestion(prev => Math.min(prev + 1, partySuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      setActiveSuggestion(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeSuggestion >= 0) {
      handleSuggestionClick(partySuggestions[activeSuggestion]);
    }
  };

  const handleMaterialChange = (index, field, value) => {
    setMaterials(prevMaterials => {
      const newMaterials = [...prevMaterials];
      newMaterials[index] = {
        ...newMaterials[index],
        [field]: value
      };
      return newMaterials;
    });
  };

  const handleMaterialNameChange = async (index, value) => {
    handleMaterialChange(index, 'name', value);
    if (value.length >= 2) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/material-names?q=${encodeURIComponent(value)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMaterialSuggestions(response.data);
        setShowMaterialSuggestions(true);
        setActiveMaterialSuggestion(-1);
      } catch (err) {
        setMaterialSuggestions([]);
        setShowMaterialSuggestions(false);
      }
    } else {
      setMaterialSuggestions([]);
      setShowMaterialSuggestions(false);
    }
  };

  const handleMaterialSuggestionClick = (index, name) => {
    handleMaterialChange(index, 'name', name);
    setShowMaterialSuggestions(false);
  };

  const handleMaterialInputBlur = () => {
    setTimeout(() => setShowMaterialSuggestions(false), 100);
  };

  const handleMaterialInputKeyDown = (e) => {
    if (!showMaterialSuggestions || materialSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setActiveMaterialSuggestion(prev => Math.min(prev + 1, materialSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      setActiveMaterialSuggestion(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeMaterialSuggestion >= 0) {
      handleMaterialSuggestionClick(0, materialSuggestions[activeMaterialSuggestion]);
    }
  };

  const handleSourceChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      source_location: value
    }));
    if (value.length >= 2) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE_URL}/api/source-locations?q=${encodeURIComponent(value)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSourceSuggestions(response.data);
        setShowSourceSuggestions(true);
        setActiveSourceSuggestion(-1);
      } catch (err) {
        setSourceSuggestions([]);
        setShowSourceSuggestions(false);
      }
    } else {
      setSourceSuggestions([]);
      setShowSourceSuggestions(false);
    }
  };

  const handleSourceSuggestionClick = (name) => {
    setFormData(prev => ({
      ...prev,
      source_location: name
    }));
    setShowSourceSuggestions(false);
  };

  const handleSourceInputBlur = () => {
    setTimeout(() => setShowSourceSuggestions(false), 100);
  };

  const handleSourceInputKeyDown = (e) => {
    if (!showSourceSuggestions || sourceSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setActiveSourceSuggestion(prev => Math.min(prev + 1, sourceSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      setActiveSourceSuggestion(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeSourceSuggestion >= 0) {
      handleSourceSuggestionClick(sourceSuggestions[activeSourceSuggestion]);
    }
  };

  const addMaterial = () => {
    setMaterials(prev => [...prev, { ...initialMaterial }]);
  };

  const removeMaterial = (index) => {
    setMaterials(prev => prev.filter((_, i) => i !== index));
  };

  const handleBillChange = (index, field, value) => {
    setBills(prevBills => {
      const newBills = [...prevBills];
      newBills[index] = { ...newBills[index], [field]: value };
      return newBills;
    });
  };

  const addBill = () => setBills(prev => [...prev, { ...initialBill }]);

  const removeBill = (index) => setBills(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Prevent negative or zero gross weight
    if (formData.gross_weight !== '' && Number(formData.gross_weight) < 0) {
      setError('Gross weight cannot be negative.');
      setLoading(false);
      return;
    }

    // Validate bills
    if (bills.some(b => !b.bill_number || !b.bill_amount)) {
      setError('All bill fields are required');
      setLoading(false);
      return;
    }

    if (materials.some(m => !m.name || !m.quantity || !m.uom)) {
      setError('All material fields are required');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Always pick current time in IST for time_in
      const now = new Date();
      const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const pad = n => n.toString().padStart(2, '0');
      const timeInIST = `${pad(istNow.getHours())}:${pad(istNow.getMinutes())}`;

      // Prepare payload
      const payload = { 
        ...formData, 
        materials,
        bills,
        serial_number: serialNumber,
        time_in: timeInIST // Always use current IST time
      };

      // Remove unused fields for other transport modes
      if (formData.transport_mode !== 'By Courier') {
        delete payload.courier_name;
        delete payload.docket_number;
      }

      await axios.post(
        `${API_BASE_URL}/api/inward-entries`,
        payload,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setSuccess('Entry created successfully!');
      setMaterials([initialMaterial]);
      setBills([initialBill]);
      setFormData(prev => ({
        ...prev,
        party_name: '',
        entry_type: '',
        transport_mode: '',
        person_name: '',
        mobile_number: '',
        vehicle_number: '',
        vehicle_type: '',
        source_location: '',
        reference_number: '',
        po_number: '',
        gross_weight: '',
        remarks: '',
        courier_name: '',
        docket_number: ''
      }));

      const serialResponse = await axios.get(
        `${API_BASE_URL}/api/inward-entries/next-serial`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSerialNumber(serialResponse.data.serial_number);

      if (onEntryCreated) onEntryCreated();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating entry');
    } finally {
      setLoading(false);
    }
  };

  const uomOptions = [
    'SqY', 'Sq.Ft', 'SqM', 'Cum', 'FTS', 'Kg', 
    'LTR', 'Mtr', 'Nos', 'PRS', 'SET','Box(es)','SM3','Sheet','Items'
  ];

  return (
    <div className="inward-entry-form">
      <h2>New Inward Entry</h2>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Serial Number</label>
            <input type="text" value={serialNumber} disabled className="readonly-input" />
          </div>
          <div className="form-group">
            <label>Entry Date</label>
            <input type="date" name="entry_date" value={formData.entry_date} disabled className="readonly-input" />
          </div>
          <div className="form-group">
            <label>Time In</label>
            <input type="time" name="time_in" value={formData.time_in} disabled className="readonly-input" readOnly />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Entry Type</label>
            <select
              name="entry_type"
              value={formData.entry_type}
              onChange={handleInputChange}
              required
              style={{ width: '220px' }}
            >
              <option value="">Select Type</option>
              <option value="Cash">Cash</option>
              <option value="Challan">Challan</option>
              <option value="Bill">Bill</option>
              <option value="RGP">RGP</option>
              <option value="FOC">FOC</option>
              <option value="RMA">RMA</option>
            </select>
          </div>
          <div className="form-group">
            <label>Transport Mode</label>
            <select
              name="transport_mode"
              value={formData.transport_mode}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Mode</option>
              <option value="By Hand">By Hand</option>
              <option value="By Vehicle">By Vehicle</option>
              <option value="By Courier">By Courier</option>
            </select>
          </div>
          <div className="form-group">
            <label>Source Location</label>
            <input
              type="text"
              name="source_location"
              value={formData.source_location}
              onChange={handleSourceChange}
              onBlur={handleSourceInputBlur}
              onKeyDown={handleSourceInputKeyDown}
              required
              placeholder="Enter source location"
              autoComplete="off"
            />
            {showSourceSuggestions && sourceSuggestions.length > 0 && (
              <ul className="party-suggestion-list">
                {sourceSuggestions.map((name, idx) => (
                  <li
                    key={idx}
                    className={`party-suggestion-item${activeSourceSuggestion === idx ? ' active' : ''}`}
                    onMouseDown={() => handleSourceSuggestionClick(name)}
                  >
                    <span>
                      {formData.source_location
                        ? (
                          <>
                            {name.substring(0, formData.source_location.length)}
                            <b>{name.substring(formData.source_location.length)}</b>
                          </>
                        )
                        : name
                      }
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {formData.transport_mode === 'By Hand' && (
          <div className="form-row">
            <div className="form-group">
              <label>Person Name</label>
              <input
                type="text"
                name="person_name"
                value={formData.person_name}
                onChange={handleInputChange}
                required
                placeholder="Enter person name"
              />
            </div>
            <div className="form-group">
              <label>Mobile Number</label>
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                pattern="[0-9]{10}"
                maxLength={10}
                inputMode="numeric"
                required
                placeholder="Enter mobile number"
                autoComplete="off"
              />
            </div>
          </div>
        )}
        {formData.transport_mode === 'By Vehicle' && (
          <div className="form-row">
            <div className="form-group">
              <label>Vehicle Number</label>
              <input
                type="text"
                name="vehicle_number"
                value={formData.vehicle_number}
                onChange={handleInputChange}
                required
                placeholder="Enter vehicle number"
              />
            </div>
            <div className="form-group">
              <label>Vehicle Type</label>
              <input
                type="text"
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleInputChange}
                required
                placeholder="Enter vehicle type"
              />
            </div>
          </div>
        )}
        {formData.transport_mode === 'By Courier' && (
          <div className="form-row">
            <div className="form-group">
              <label>Courier Name</label>
              <input
                type="text"
                name="courier_name"
                value={formData.courier_name}
                onChange={handleInputChange}
                required
                placeholder="Enter courier name"
              />
            </div>
            <div className="form-group">
              <label>Docket Number</label>
              <input
                type="text"
                name="docket_number"
                value={formData.docket_number}
                onChange={handleInputChange}
                required
                placeholder="Enter docket number"
              />
            </div>
          </div>
        )}
        <div className="form-row">
          {formData.entry_type === 'Challan' && (
            <div className="form-group">
              <label>Reference Number (Challan)</label>
              <input
                type="text"
                name="reference_number"
                value={formData.reference_number}
                onChange={handleInputChange}
                placeholder="Enter Challan/Reference No."
              />
            </div>
          )}
          <div className="form-group">
            <label>PO Number (optional)</label>
            <input
              type="text"
              name="po_number"
              value={formData.po_number}
              onChange={handleInputChange}
              placeholder="Enter PO Number"
            />
          </div>
          <div className="form-group">
            <label>Gross Weight</label>
            <input
              type="number"
              name="gross_weight"
              value={formData.gross_weight}
              onChange={handleInputChange}
              placeholder="Enter gross weight"
              style={{ width: '120px' }}
              min="0"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group party-name-advanced">
            <label>
              <i className="fas fa-building" style={{ marginRight: 6, color: '#6366f1' }}></i>
              Party Name
            </label>
            <input
              type="text"
              name="party_name"
              value={formData.party_name}
              onChange={handlePartyNameChange}
              onBlur={handlePartyInputBlur}
              onKeyDown={handlePartyInputKeyDown}
              required
              autoComplete="off"
              className="party-name-input"
              style={{ paddingLeft: '2rem', width: '220px' }}
              placeholder="Type to search or add party"
            />
            {showSuggestions && partySuggestions.length > 0 && (
              <ul className="party-suggestion-list">
                {partySuggestions.map((name, idx) => (
                  <li
                    key={idx}
                    className={`party-suggestion-item${activeSuggestion === idx ? ' active' : ''}`}
                    onMouseDown={() => handleSuggestionClick(name)}
                  >
                    <i className="fas fa-user-tag" style={{ marginRight: 8, color: '#6366f1' }}></i>
                    <span>
                      {formData.party_name
                        ? (
                          <>
                            {name.substring(0, formData.party_name.length)}
                            <b>{name.substring(formData.party_name.length)}</b>
                          </>
                        )
                        : name
                      }
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {/* Bills Section - moved after PO Number and Gross Weight */}
        <div className="bills-section" style={{ marginTop: 0 }}>
          <h3>Bills</h3>
          {bills.map((bill, index) => (
            <div key={index} className="bill-row" style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <div className="form-group">
                <label>Bill Number</label>
                <input
                  type="text"
                  value={bill.bill_number}
                  onChange={e => handleBillChange(index, 'bill_number', e.target.value)}
                  required
                  placeholder="Enter bill number"
                />
              </div>
              <div className="form-group">
                <label>Bill Amount</label>
                <input
                  type="number"
                  value={bill.bill_amount}
                  onChange={e => handleBillChange(index, 'bill_amount', e.target.value)}
                  required
                  placeholder="Enter bill amount"
                />
              </div>
              {bills.length > 1 && (
                <button type="button" className="remove-bill-button" onClick={() => removeBill(index)}>
                  Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" className="add-bill-button" onClick={addBill} style={addBillButtonStyles}
            onMouseOver={e => e.currentTarget.style.background = 'linear-gradient(90deg, #818cf8 0%, #6366f1 100%)'}
            onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(90deg, #6366f1 0%, #818cf8 100%)'}
          >
            Add Bill
          </button>
        </div>
        <div className="materials-section">
          <h3>Materials</h3>
          {materials.map((material, index) => (
            <div key={index} className="material-row">
              <div className="form-row">
                <div className="form-group">
                  <label>Material Name</label>
                  <input
                    type="text"
                    value={material.name}
                    onChange={(e) => handleMaterialNameChange(index, e.target.value)}
                    onBlur={handleMaterialInputBlur}
                    onKeyDown={handleMaterialInputKeyDown}
                    required
                  />
                  {showMaterialSuggestions && materialSuggestions.length > 0 && (
                    <ul className="party-suggestion-list">
                      {materialSuggestions.map((name, idx) => (
                        <li
                          key={idx}
                          className={`party-suggestion-item${activeMaterialSuggestion === idx ? ' active' : ''}`}
                          onMouseDown={() => handleMaterialSuggestionClick(index, name)}
                        >
                          <span>
                            {material.name
                              ? (
                                <>
                                  {name.substring(0, material.name.length)}
                                  <b>{name.substring(material.name.length)}</b>
                                </>
                              )
                              : name
                            }
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={material.quantity}
                    onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>UoM</label>
                  <select
                    value={material.uom}
                    onChange={(e) => handleMaterialChange(index, 'uom', e.target.value)}
                    required
                  >
                    <option value="">Select UoM</option>
                    {uomOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              {materials.length > 1 && (
                <button
                  type="button"
                  className="remove-material-button"
                  onClick={() => removeMaterial(index)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="add-material-button"
            onClick={addMaterial}
          >
            Add Material
          </button>
        </div>
        <div className="form-group">
          <label>Remarks</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleInputChange}
            rows="4"
            placeholder="Enter remarks"
          />
        </div>
        <button
          type="submit"
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Entry'}
        </button>
      </form>
    </div>
  );
};

export default InwardEntryForm;